import React, { useEffect, useRef, useState } from 'react';
import { Select, Spin, message } from 'antd';
import * as echarts from 'echarts';
import { getLeadTrend, LeadTrendData } from '../../../services/statistics';

const { Option } = Select;

const LeadTrendChart: React.FC = () => {
  const chartRef = useRef<HTMLDivElement>(null);
  const chartInstance = useRef<echarts.ECharts | null>(null);
  const [loading, setLoading] = useState(false);
  const [days, setDays] = useState(7);
  const [chartData, setChartData] = useState<LeadTrendData | null>(null);

  // 加载数据
  const loadData = async (selectedDays: number) => {
    setLoading(true);
    try {
      const data = await getLeadTrend(selectedDays);
      setChartData(data);
    } catch (error) {
      console.error('Failed to load lead trend data:', error);
      message.error('加载数据失败');
    } finally {
      setLoading(false);
    }
  };

  // 初始加载
  useEffect(() => {
    loadData(days);
  }, []);

  // 时间维度变化时重新加载
  const handleDaysChange = (value: number) => {
    setDays(value);
    loadData(value);
  };

  // 渲染图表
  useEffect(() => {
    if (!chartRef.current || !chartData) return;

    // Initialize chart
    if (!chartInstance.current) {
      chartInstance.current = echarts.init(chartRef.current);
    }

    const option = {
      tooltip: {
        trigger: 'axis',
        axisPointer: {
          type: 'cross',
        },
      },
      legend: {
        data: ['新增线索', '转化线索', '转化率'],
      },
      grid: {
        left: '3%',
        right: '4%',
        bottom: '3%',
        containLabel: true,
      },
      xAxis: {
        type: 'category',
        boundaryGap: false,
        data: chartData.dates,
      },
      yAxis: [
        {
          type: 'value',
          name: '数量',
          position: 'left',
        },
        {
          type: 'value',
          name: '转化率(%)',
          position: 'right',
          axisLabel: {
            formatter: '{value}%',
          },
        },
      ],
      series: [
        {
          name: '新增线索',
          type: 'line',
          data: chartData.new_leads,
          smooth: true,
          itemStyle: {
            color: '#1890ff',
          },
          areaStyle: {
            color: new echarts.graphic.LinearGradient(0, 0, 0, 1, [
              { offset: 0, color: 'rgba(24, 144, 255, 0.3)' },
              { offset: 1, color: 'rgba(24, 144, 255, 0.05)' },
            ]),
          },
        },
        {
          name: '转化线索',
          type: 'line',
          data: chartData.converted_leads,
          smooth: true,
          itemStyle: {
            color: '#52c41a',
          },
        },
        {
          name: '转化率',
          type: 'line',
          yAxisIndex: 1,
          data: chartData.conversion_rates,
          smooth: true,
          itemStyle: {
            color: '#faad14',
          },
        },
      ],
    };

    chartInstance.current.setOption(option);

    // Handle resize
    const handleResize = () => {
      chartInstance.current?.resize();
    };
    window.addEventListener('resize', handleResize);

    return () => {
      window.removeEventListener('resize', handleResize);
    };
  }, [chartData]);

  return (
    <Spin spinning={loading}>
      <div style={{ marginBottom: 16, textAlign: 'right' }}>
        <Select
          value={days}
          onChange={handleDaysChange}
          style={{ width: 120 }}
        >
          <Option value={7}>近7天</Option>
          <Option value={15}>近15天</Option>
          <Option value={30}>近30天</Option>
          <Option value={90}>近90天</Option>
        </Select>
      </div>
      <div ref={chartRef} style={{ width: '100%', height: '300px' }} />
    </Spin>
  );
};

export default LeadTrendChart;
